/**
 * Die Code-Erkennung -- nativ, wo es sie vollständig gibt.
 *
 * Chrome auf Android bringt `BarcodeDetector` selbst mit. Dort ist die
 * Erkennung deutlich schneller als WebAssembly und liest Etiketten auch
 * schräg.
 *
 * Wo sie fehlt (Safari, Firefox, Chrome auf Windows) **oder nicht alle
 * Formate kann**, springt das Ponyfill mit ZXing-C++ als WebAssembly ein. Die
 * Etiketten des Ortsverbands tragen DataMatrix; eine eingebaute Erkennung, die
 * das nicht liest, ist für uns keine.
 *
 * Das Ponyfill wird **nachgeladen und nur dann**. Sonst trüge jedes Handy
 * rund 1 MB WebAssembly mit sich herum, die es nie braucht.
 */

/**
 * Die Formate, die auf THW-Material vorkommen.
 *
 * DataMatrix auf den Inventaretiketten, QR auf unseren Lagerort-Etiketten,
 * die Strichcodes auf Herstelleretiketten. Weniger Formate heißt mehr Zeit je
 * Format und weniger Fehlerkennungen -- dieselbe Liste wie im
 * Inventur-Werkzeug.
 */
export const FORMATE = [
  "data_matrix",
  "qr_code",
  "code_128",
  "code_39",
  "code_93",
  "ean_13",
] as const;

export interface Erkenner {
  detect(quelle: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

interface ErkennerBauart {
  new (optionen: { formats: readonly string[] }): Erkenner;
  getSupportedFormats(): Promise<readonly string[]>;
}

let geladen: Promise<ErkennerBauart> | null = null;

/** Kann die eingebaute Erkennung alles, was wir brauchen? */
async function eingebautGenuegt(bauart: ErkennerBauart): Promise<boolean> {
  try {
    const kann = await bauart.getSupportedFormats();
    return FORMATE.every((f) => kann.includes(f));
  } catch {
    return false;
  }
}

/**
 * Das Ponyfill, mit der WebAssembly-Datei **von unserem eigenen Server**.
 *
 * Von sich aus holt ZXing sie von einem CDN im Internet. An der
 * Einsatzstelle gibt es kein Internet -- ein iPhone könnte dort nichts
 * scannen.
 */
async function ponyfillLaden(): Promise<ErkennerBauart> {
  const [{ BarcodeDetector, prepareZXingModule }, { default: wasmAdresse }] = await Promise.all([
    import("barcode-detector/ponyfill"),
    import("zxing-wasm/reader/zxing_reader.wasm?url"),
  ]);

  prepareZXingModule({
    overrides: {
      locateFile: (pfad: string, praefix: string) =>
        pfad.endsWith(".wasm") ? wasmAdresse : praefix + pfad,
    },
    // Gleich laden, nicht erst beim ersten Bild: Dann ist die Datei auch im
    // Zwischenspeicher des Browsers, bevor das Netz wegfällt.
    fireImmediately: true,
  }).catch(() => {
    // Scheitert das Vorladen, versucht es der erste Erkennungsversuch noch
    // einmal -- und die Schleife meldet sich dann ohnehin nicht mit Treffern.
  });

  return BarcodeDetector as unknown as ErkennerBauart;
}

/**
 * Liefert die Erkennerklasse -- die eingebaute oder die nachgeladene.
 *
 * Das Ergebnis wird gemerkt: Ein zweiter Scan soll nicht wieder auf einen
 * Download warten.
 */
export function erkennerBauart(): Promise<ErkennerBauart> {
  geladen ??= (async () => {
    const eingebaut = (globalThis as { BarcodeDetector?: ErkennerBauart }).BarcodeDetector;
    if (eingebaut !== undefined && (await eingebautGenuegt(eingebaut))) return eingebaut;
    return ponyfillLaden();
  })();
  return geladen;
}

/** Bringt die Erkennung ins Rollen, bevor jemand auf „Kamera starten" drückt. */
export function erkennerVorbereiten(): void {
  void erkennerBauart();
}
